import { useCallback, useEffect, useRef, useState } from "react";
import SystemMap from "@/components/story/SystemMap";
import ParticleField from "@/components/story/ParticleField";
import { prefersReducedMotion, canAnimateHeavy } from "@/lib/motion";

/**
 * The hero visual, in two layers that agree with each other.
 *
 * Layer 1, always: the SVG system map. Drawn on the first paint, readable by
 * a screen reader and by a crawler, and it stays on screen for the whole
 * time. It is the crisp statement of the diagram.
 *
 * Layer 2, on desktop with motion allowed: a Canvas 2D flow field that
 * assembles from scattered specks onto the exact same curves and then keeps
 * running along them toward the outcome. It is additive. It never replaces
 * the hairlines, the dots, the frame or a single word.
 *
 * three.js used to draw layer 2 and was code-split behind an idle callback
 * for that reason. It is gone: the replacement is about 1.5 kB and needs no
 * chunk, no idle gate and no connection check, so the layer is simply
 * mounted when it is allowed to run. Both layers now start their timelines
 * on the same mount, which is why the specks land as the hairline draws
 * instead of the old version's dissolve-then-reassemble.
 *
 * If the canvas never produces a frame, or a GPU-less browser refuses a 2D
 * context, nothing changes on screen: the map is already there. The hero has
 * no state in which it is empty.
 */

export default function HeroVisual() {
  const [flow, setFlow] = useState(false);
  const [on, setOn] = useState(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    /* Same gate as before: reduced motion and small screens get the SVG on
       its own. The check runs once, on mount, so the two layers cannot get
       out of step with each other. */
    if (!prefersReducedMotion() && canAnimateHeavy()) setFlow(true);
    return () => { mounted.current = false; };
  }, []);

  const onReady = useCallback(() => {
    if (mounted.current) setOn(true);
  }, []);

  return (
    <div className="hero-visual" data-flow={on ? "on" : "off"} data-testid="hero-visual">
      <div className="hero-visual__layer hero-visual__layer--svg">
        <SystemMap />
      </div>

      {flow ? (
        <div className="hero-visual__layer hero-visual__layer--flow">
          <ParticleField onReady={onReady} />
        </div>
      ) : null}
    </div>
  );
}
